/**
 * Journal d'un chantier, jour par jour et ouvrier par ouvrier.
 *
 * <p>Les heures pointées, les rapports écrits et les photos prises sur un
 * chantier vivent dans des listes séparées, comme le matériel pris au dépôt.
 * Le patron veut les lire ensemble : pour chaque journée, qui était là, de
 * quelle heure à quelle heure, ce qu'il a fait, le matériel qu'il a pris et à
 * quelle heure, et les photos de ce jour-là.
 */

import { hours } from './utils';

/** « 12.10.2026 » ou « 2026-10-12 » → « 2026-10-12 ». */
export function dateIso(d) {
  const s = String(d || '').trim();
  const fr = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (fr) return fr[3] + '-' + fr[2].padStart(2, '0') + '-' + fr[1].padStart(2, '0');
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return iso ? iso[1] + '-' + iso[2] + '-' + iso[3] : '';
}

const minuscule = (s) =>
  String(s || '')
    .trim()
    .toLowerCase();

/**
 * Regroupe le suivi du chantier par journée puis par ouvrier.
 *
 * <p>Une photo n'est rattachée qu'au nom de son auteur : elle rejoint
 * l'ouvrier de ce nom ce jour-là, ou reste à part sous le nom inscrit.
 *
 * @param {object} ch chantier, avec `pointages`, `rapports` et `photos`
 * @param {object[]} [employes] fiches d'employé, pour les noms
 * @returns {{date: string, ouvriers: object[]}[]} les journées, la plus récente d'abord
 */
export function journalDuChantier(ch, employes = []) {
  const jours = new Map();
  const nomDe = (empId, defaut) => {
    const e = (employes || []).find((x) => x.id === empId);
    if (!e) return defaut || '';
    return [e.prenom, e.nom].filter(Boolean).join(' ') || defaut || '';
  };
  const ouvrier = (date, cle, nom, empId) => {
    if (!jours.has(date)) jours.set(date, new Map());
    const j = jours.get(date);
    if (!j.has(cle))
      j.set(cle, {
        cle,
        empId: empId ?? null,
        nom,
        pointages: [],
        rapports: [],
        photos: [],
        materiel: [],
      });
    return j.get(cle);
  };
  const cleDe = (empId, nom) => (empId != null ? 'e' + empId : 'n' + minuscule(nom));

  (ch?.pointages || []).forEach((p) => {
    const date = dateIso(p.date);
    if (!date) return;
    const nom = nomDe(p.empId, p.empNom);
    ouvrier(date, cleDe(p.empId, nom), nom, p.empId).pointages.push({
      ...p,
      heures: p.type === 'absence' ? 0 : p.sortie ? hours(p.entree, p.sortie, +p.pause || 0) : 0,
    });
  });
  (ch?.rapports || []).forEach((r) => {
    const date = dateIso(r.date);
    if (!date) return;
    const nom = nomDe(r.empId, r.empNom);
    ouvrier(date, cleDe(r.empId, nom), nom, r.empId).rapports.push(r);
  });
  // L'auteur inscrit est le nom de l'ouvrier, ou son prénom et son nom : la
  // photo ou la sortie du dépôt rejoint l'ouvrier de ce nom ce jour-là.
  const ouvrierNomme = (date, nomInscrit) => {
    const auteur = minuscule(nomInscrit);
    const j = jours.get(date);
    const deCeJour =
      auteur &&
      j &&
      [...j.values()].find((o) => {
        const e = (employes || []).find((x) => x.id === o.empId);
        const homonymes = (employes || []).filter((x) => minuscule(x.nom) === minuscule(e?.nom));
        const noms = [o.nom, [e?.prenom, e?.nom].filter(Boolean).join(' ')];
        if (homonymes.length <= 1) noms.push(e?.nom);
        return noms.map(minuscule).includes(auteur);
      });
    if (deCeJour) return deCeJour;
    // Le prénom et le nom d'abord ; le nom de famille seul ne vaut que s'il
    // ne désigne qu'une personne — deux « Exemple » se confondaient.
    const complet = (employes || []).find(
      (x) => auteur && minuscule([x.prenom, x.nom].filter(Boolean).join(' ')) === auteur,
    );
    const parNom = (employes || []).filter((x) => auteur && minuscule(x.nom) === auteur);
    const e = complet || (parNom.length === 1 ? parNom[0] : null);
    if (e) return ouvrier(date, cleDe(e.id), nomDe(e.id, nomInscrit), e.id);
    return ouvrier(date, cleDe(null, nomInscrit || '—'), nomInscrit || '—', null);
  };
  (ch?.photos || []).forEach((ph) => {
    const date = dateIso(ph.date);
    if (!date) return;
    ouvrierNomme(date, ph.auteur).photos.push(ph);
  });
  (ch?.materiaux || [])
    .filter((m) => m.source !== 'fournisseur')
    .forEach((m) => {
      const date = dateIso(m.date);
      if (!date) return;
      ouvrierNomme(date, m.empNom).materiel.push(m);
    });

  return [...jours.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([date, ouvriers]) => ({
      date,
      ouvriers: [...ouvriers.values()]
        .map((o) => ({
          ...o,
          pointages: [...o.pointages].sort((a, b) =>
            String(a.entree).localeCompare(String(b.entree)),
          ),
          photos: [...o.photos].sort((a, b) => String(a.h).localeCompare(String(b.h))),
          materiel: [...o.materiel].sort((a, b) => String(a.heure).localeCompare(String(b.heure))),
          total: o.pointages.reduce((s, p) => s + (p.heures || 0), 0),
          taches: [
            ...new Set(
              [...o.pointages.map((p) => p.tache), ...o.rapports.map((r) => r.contenu)]
                .map((t) => String(t || '').trim())
                .filter(Boolean),
            ),
          ],
        }))
        .sort((a, b) => a.nom.localeCompare(b.nom)),
    }));
}
